import { Link } from "react-router-dom";
import { CheckCircle2, Phone, ArrowLeft, Mail, Clock } from "lucide-react";

export default function ThankYou() {
    return (
        <div
            data-testid="thank-you-page"
            className="min-h-screen bg-neutral-950 text-white"
        >
            <header className="border-b border-white/10 bg-neutral-950/85 backdrop-blur">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src="/precisionlogo.png"
                            alt="Precision Care Transport"
                            className="h-14 w-14 object-contain"
                        />
                        <span className="hidden sm:block">
                            <p className="text-xs uppercase tracking-[0.35em] text-red-400">
                                Precision Care Transport
                            </p>
                            <h1 className="text-lg font-semibold">
                                Elite Medical Transportation
                            </h1>
                        </span>
                    </Link>
                    <Link
                        to="/"
                        data-testid="thank-you-home-link"
                        className="inline-flex items-center gap-2 text-sm text-white/80 transition hover:text-white"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to site
                    </Link>
                </div>
            </header>

            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.24),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.06),transparent_22%)]" />
                <div className="relative mx-auto max-w-3xl px-6 py-24 md:py-32 text-center">
                    <div
                        data-testid="thank-you-icon"
                        className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-600/20 ring-1 ring-red-500/30"
                    >
                        <CheckCircle2 className="h-10 w-10 text-red-400" />
                    </div>

                    <p className="mt-8 inline-flex rounded-full border border-red-500/30 bg-red-500/10 px-4 py-1 text-sm text-red-300">
                        Request received
                    </p>
                    <h2 className="mt-5 text-4xl font-bold leading-tight md:text-6xl">
                        Thank you for contacting Precision Care Transport.
                    </h2>
                    <p className="mt-6 text-lg leading-8 text-white/75">
                        Your transportation request has been sent to our
                        dispatch team. A member of our team will review your
                        details and reach out shortly to confirm your ride.
                        You'll also receive a confirmation email at the
                        address you provided.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <a
                            href="tel:+19809254006"
                            data-testid="thank-you-call-btn"
                            className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-6 py-3 text-base font-semibold shadow-xl shadow-red-950/40 transition hover:bg-red-500"
                        >
                            <Phone className="h-4 w-4" />
                            Call 980-925-4006
                        </a>
                        <Link
                            to="/"
                            data-testid="thank-you-return-btn"
                            className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-6 py-3 text-base font-semibold transition hover:bg-white/10"
                        >
                            Return to homepage
                        </Link>
                    </div>

                    <div className="mt-16 grid gap-4 text-left sm:grid-cols-3">
                        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
                            <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-red-600/20 text-red-400">
                                <Mail className="h-5 w-5" />
                            </div>
                            <p className="text-sm font-semibold">Email</p>
                            <a
                                href="mailto:info@precisioncaretransport.com"
                                className="mt-1 block text-sm text-white/70 underline decoration-red-500/60 hover:text-white"
                            >
                                info@precisioncaretransport.com
                            </a>
                        </div>
                        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
                            <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-red-600/20 text-red-400">
                                <Phone className="h-5 w-5" />
                            </div>
                            <p className="text-sm font-semibold">Phone</p>
                            <a
                                href="tel:+19809254006"
                                className="mt-1 block text-sm text-white/70 underline decoration-red-500/60 hover:text-white"
                            >
                                980-925-4006
                            </a>
                        </div>
                        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
                            <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-red-600/20 text-red-400">
                                <Clock className="h-5 w-5" />
                            </div>
                            <p className="text-sm font-semibold">Hours</p>
                            <p className="mt-1 text-sm text-white/70 leading-6">
                                Mon–Fri: 5 AM – 7 PM
                                <br />
                                Sat: 7 AM – 3 PM
                                <br />
                                Sun: By appointment
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="border-t border-white/10 bg-neutral-950">
                <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-8 text-sm text-white/55 md:flex-row md:items-center md:justify-between">
                    <p>
                        &copy; {new Date().getFullYear()} Precision Care
                        Transport. All rights reserved.
                    </p>
                    <p>Safe, dependable transportation with dignity and care.</p>
                </div>
            </footer>
        </div>
    );
}
